import { useState, useEffect } from 'react';
import { isNative } from '../utils/platform';

/**
 * Network Status Service
 * Monitors online/offline status and provides reactive updates
 */

class NetworkStatusService {
  constructor() {
    this.isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
    this.listeners = new Set();
    this.wasOffline = false;

    // Bind event handlers
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.handleOnline);
      window.addEventListener('offline', this.handleOffline);
    }

    // Native: back the singleton with @capacitor/network, which is more
    // reliable than navigator.onLine inside a WebView. No-op on web.
    if (isNative) this.initNative();
  }

  async initNative() {
    try {
      const { Network } = await import('@capacitor/network');
      const status = await Network.getStatus();
      this.isOnline = status.connected;
      Network.addListener('networkStatusChange', (s) => {
        if (s.connected) this.handleOnline();
        else this.handleOffline();
      });
    } catch {
      // plugin unavailable — fall back to navigator.onLine / window events
    }
  }

  handleOnline = () => {
    this.isOnline = true;
    const wasOffline = this.wasOffline;
    this.wasOffline = false;
    this.notifyListeners({ isOnline: true, wasOffline });
  };

  handleOffline = () => {
    this.isOnline = false;
    this.wasOffline = true;
    this.notifyListeners({ isOnline: false, wasOffline: false });
  };

  notifyListeners(status) {
    this.listeners.forEach(callback => {
      try {
        callback(status);
      } catch (error) {
        console.error('[NetworkStatus] Listener error:', error);
      }
    });
  }

  /**
   * Subscribe to network status changes
   * @param {Function} callback - Called with { isOnline: boolean, wasOffline: boolean }
   * @returns {Function} - Unsubscribe function
   */
  subscribe(callback) {
    this.listeners.add(callback);

    // Return unsubscribe function
    return () => {
      this.listeners.delete(callback);
    };
  }

  /**
   * Get current online status
   * @returns {boolean}
   */
  getStatus() {
    return this.isOnline;
  }

  /**
   * Check if we just came back online
   * @returns {boolean}
   */
  justCameOnline() {
    return this.isOnline && this.wasOffline;
  }

  /**
   * Perform a health check by pinging the API
   * @param {string} healthCheckUrl - URL to ping (optional)
   * @returns {Promise<boolean>}
   */
  async healthCheck(healthCheckUrl = '/api/health') {
    if (!this.isOnline) {
      return false;
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(healthCheckUrl, {
        method: 'HEAD',
        cache: 'no-store',
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      return response.ok;
    } catch (error) {
      return false;
    }
  }

  /**
   * Cleanup event listeners
   */
  destroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.handleOnline);
      window.removeEventListener('offline', this.handleOffline);
    }
    this.listeners.clear();
  }
}

// Singleton instance
export const networkStatus = new NetworkStatusService();

/**
 * React hook for network status
 */
export function useNetworkStatus() {
  const [status, setStatus] = useState({
    isOnline: networkStatus.getStatus(),
    wasOffline: false
  });

  useEffect(() => {
    const unsubscribe = networkStatus.subscribe((newStatus) => {
      setStatus(newStatus);
    });

    return unsubscribe;
  }, []);

  return status;
}

export default networkStatus;
